<template>
  <div class="form-step">
    <!-- Education Section -->
    <div class="section-header d-flex justify-content-between align-items-center">
      <h5 class="section-title">Education</h5>
      <button type="button" class="btn btn-outline-primary" @click="addEducation">
        <i class="bi bi-plus me-2"></i>Add Education
      </button>
    </div>

    <div v-if="educationDraft" class="education-item mb-4 p-3 border rounded bg-light-subtle">
      <div class="row my-3">
        <div class="col-md-4">
          <label class="form-label">Institution</label>
          <input
            ref="newEducationInstitutionInput"
            type="text"
            class="form-control"
            :class="{ 'field-invalid': isEducationFieldInvalid(educationDraft, 'institution', null, true) }"
            v-model="educationDraft.institution"
            @input="handleEducationDraftInput"
          >
        </div>
        <div class="col-md-4">
          <label class="form-label">Country</label>
          <select
            class="form-select"
            :class="{ 'field-invalid': isEducationFieldInvalid(educationDraft, 'country', null, true) }"
            v-model="educationDraft.country"
            @change="handleEducationDraftInput"
          >
            <option value="">Select Country</option>
            <option v-for="country in africanCountries" :key="country.value" :value="country.value">
              {{ country.label }}
            </option>
          </select>
        </div>
        <div class="col-md-4">
          <label class="form-label">Year</label>
          <input
            type="date"
            class="form-control"
            :class="{ 'field-invalid': isEducationFieldInvalid(educationDraft, 'year', null, true) }"
            v-model="educationDraft.year"
            @input="handleEducationDraftInput"
          >
        </div>
      </div>
      <div class="row mb-3">
        <div class="col-md-12">
          <label class="form-label">Degree</label>
          <input
            type="text"
            class="form-control"
            :class="{ 'field-invalid': isEducationFieldInvalid(educationDraft, 'degree', null, true) }"
            v-model="educationDraft.degree"
            @input="handleEducationDraftInput"
          >
        </div>
      </div>
      <div class="row">
        <div class="col-md-12 d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-outline-secondary" @click="cancelNewEducation">Cancel</button>
          <button type="button" class="btn btn-outline-primary" @click="saveNewEducation">Save</button>
        </div>
      </div>
    </div>

    <div v-for="(edu, index) in education" :key="`edu-${educationKey(edu, index)}`" class="education-item mb-4 p-3 border rounded">
      <div
        class="education-header d-flex justify-content-between align-items-center"
        role="button"
        tabindex="0"
        @click="toggleEducation(educationKey(edu, index))"
        @keydown.enter.prevent="toggleEducation(educationKey(edu, index))"
        @keydown.space.prevent="toggleEducation(educationKey(edu, index))"
      >
        <h6 class="mb-0 border-0 pb-0">{{ educationHeaderTitles[educationKey(edu, index)] }}</h6>
        <i class="bi" :class="isEducationExpanded(educationKey(edu, index)) ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
      </div>
      <div v-if="isEducationExpanded(educationKey(edu, index))" class="row my-3">
        <div class="col-md-4">
          <label class="form-label">Institution</label>
          <input
            type="text"
            class="form-control"
            :class="{ 'field-invalid': isEducationFieldInvalid(edu, 'institution', index) }"
            v-model="edu.institution"
            @input="markEducationDirty(index)"
          >
        </div>
        <div class="col-md-4">
          <label class="form-label">Country</label>
          <select
            class="form-select"
            :class="{ 'field-invalid': isEducationFieldInvalid(edu, 'country', index) }"
            v-model="edu.country"
            @change="markEducationDirty(index)"
          >
            <option value="">Select Country</option>
            <option v-for="country in africanCountries" :key="country.value" :value="country.value">
              {{ country.label }}
            </option>
          </select>
        </div>
        <div class="col-md-4">
          <label class="form-label">Year</label>
          <input
            type="date"
            class="form-control"
            :class="{ 'field-invalid': isEducationFieldInvalid(edu, 'year', index) }"
            v-model="edu.year"
            @input="markEducationDirty(index)"
          >
        </div>
      </div>
      <div v-if="isEducationExpanded(educationKey(edu, index))" class="row mb-3">
        <div class="col-md-12">
          <label class="form-label">Degree</label>
          <input
            type="text"
            class="form-control"
            :class="{ 'field-invalid': isEducationFieldInvalid(edu, 'degree', index) }"
            v-model="edu.degree"
            @input="markEducationDirty(index)"
          >
        </div>
      </div>
      <div v-if="isEducationExpanded(educationKey(edu, index))" class="row">
        <div class="col-md-12 d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-sm btn-outline-primary" :disabled="!educationDirty[index]" @click="saveEducation(index)">
            <i class="bi bi-check me-1"></i>Save
          </button>
          <button type="button" class="btn btn-sm btn-outline-danger" @click="removeEducation(index)">
            <i class="bi bi-x me-1"></i>Remove
          </button>
        </div>
      </div>
    </div>

    <!-- Board/Editorial Membership Section -->
    <div class="section-header d-flex justify-content-between align-items-center">
      <h5 class="section-title">Board Memberships & Leadership Roles</h5>
      <button type="button" class="btn btn-outline-primary" @click="addBoardMembership">
        <i class="bi bi-plus me-2"></i>Add Role
      </button>
    </div>

    <div v-if="boardDraft" class="board-item mb-4 p-3 border rounded bg-light-subtle">
      <div class="row my-3">
        <div class="col-md-6">
          <label class="form-label">Committee/board</label>
          <input
            ref="newBoardCommitteeInput"
            type="text"
            class="form-control"
            :class="{ 'field-invalid': isBoardFieldInvalid(boardDraft, 'committee', null, true) }"
            v-model="boardDraft.committee"
            @input="handleBoardDraftInput"
          >
        </div>
        <div class="col-md-6">
          <label class="form-label">Role</label>
          <input
            type="text"
            class="form-control"
            :class="{ 'field-invalid': isBoardFieldInvalid(boardDraft, 'role', null, true) }"
            v-model="boardDraft.role"
            @input="handleBoardDraftInput"
          >
        </div>
      </div>
      <div class="row mb-3">
        <div class="col-md-6">
          <label class="form-label">Start Date</label>
          <input
            type="date"
            class="form-control"
            :class="{ 'field-invalid': isBoardFieldInvalid(boardDraft, 'startDate', null, true) }"
            v-model="boardDraft.startDate"
            @input="handleBoardDraftInput"
          >
        </div>
        <div class="col-md-6">
          <label class="form-label">End Date</label>
          <input
            type="date"
            class="form-control"
            :class="{ 'field-invalid': isBoardFieldInvalid(boardDraft, 'endDate', null, true) }"
            v-model="boardDraft.endDate"
            @input="handleBoardDraftInput"
          >
        </div>
      </div>
      <div class="row">
        <div class="col-md-12 d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-outline-secondary" @click="cancelNewBoardMembership">Cancel</button>
          <button type="button" class="btn btn-outline-primary" @click="saveNewBoardMembership">Save</button>
        </div>
      </div>
    </div>

    <div v-for="(membership, index) in boardMemberships" :key="`board-${boardKey(membership, index)}`" class="board-item mb-4 p-3 border rounded">
      <div
        class="board-header d-flex justify-content-between align-items-center"
        role="button"
        tabindex="0"
        @click="toggleBoard(boardKey(membership, index))"
        @keydown.enter.prevent="toggleBoard(boardKey(membership, index))"
        @keydown.space.prevent="toggleBoard(boardKey(membership, index))"
      >
        <h6 class="mb-0 border-0 pb-0">{{ boardHeaderTitles[boardKey(membership, index)] }}</h6>
        <i class="bi" :class="isBoardExpanded(boardKey(membership, index)) ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
      </div>
      <div v-if="isBoardExpanded(boardKey(membership, index))" class="row my-3">
        <div class="col-md-6">
          <label class="form-label">Committee/board</label>
          <input
            type="text"
            class="form-control"
            :class="{ 'field-invalid': isBoardFieldInvalid(membership, 'committee', index) }"
            v-model="membership.committee"
            @input="markBoardDirty(index)"
          >
        </div>
        <div class="col-md-6">
          <label class="form-label">Role</label>
          <input
            type="text"
            class="form-control"
            :class="{ 'field-invalid': isBoardFieldInvalid(membership, 'role', index) }"
            v-model="membership.role"
            @input="markBoardDirty(index)"
          >
        </div>
      </div>
      <div v-if="isBoardExpanded(boardKey(membership, index))" class="row mb-3">
        <div class="col-md-6">
          <label class="form-label">Start Date</label>
          <input
            type="date"
            class="form-control"
            :class="{ 'field-invalid': isBoardFieldInvalid(membership, 'startDate', index) }"
            v-model="membership.startDate"
            @input="markBoardDirty(index)"
          >
        </div>
        <div class="col-md-6">
          <label class="form-label">End Date</label>
          <input
            type="date"
            class="form-control"
            :class="{ 'field-invalid': isBoardFieldInvalid(membership, 'endDate', index) }"
            v-model="membership.endDate"
            @input="markBoardDirty(index)"
          >
        </div>
      </div>
      <div v-if="isBoardExpanded(boardKey(membership, index))" class="row">
        <div class="col-md-12 d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-sm btn-outline-primary" :disabled="!boardDirty[index]" @click="saveBoardMembership(index)">
            <i class="bi bi-check me-1"></i>Save
          </button>
          <button type="button" class="btn btn-sm btn-outline-danger" @click="removeBoardMembership(index)">
            <i class="bi bi-x me-1"></i>Remove
          </button>
        </div>
      </div>
    </div>

    <!-- Professional Positions Section -->
    <div class="section-header d-flex justify-content-between align-items-center">
      <h5 class="section-title">Professional Experience</h5>
      <button type="button" class="btn btn-outline-primary" @click="addProfessionalPosition">
        <i class="bi bi-plus me-2"></i>Add Experience
      </button>
    </div>

    <div v-if="positionDraft" class="position-item mb-4 p-3 border rounded bg-light-subtle">
      <div class="row my-3">
        <div class="col-md-12">
          <label class="form-label">Position</label>
          <input
            ref="newPositionNameInput"
            type="text"
            class="form-control"
            :class="{ 'field-invalid': isPositionFieldInvalid(positionDraft, 'position', null, true) }"
            v-model="positionDraft.position"
            @input="handlePositionDraftInput"
          >
        </div>
      </div>
      <div class="row mb-3">
        <div class="col-md-6">
          <label class="form-label">Start Date</label>
          <input
            type="date"
            class="form-control"
            :class="{ 'field-invalid': isPositionFieldInvalid(positionDraft, 'startDate', null, true) }"
            v-model="positionDraft.startDate"
            @input="handlePositionDraftInput"
          >
        </div>
        <div class="col-md-6">
          <label class="form-label">End Date</label>
          <input
            type="date"
            class="form-control"
            :class="{ 'field-invalid': isPositionFieldInvalid(positionDraft, 'endDate', null, true) }"
            v-model="positionDraft.endDate"
            @input="handlePositionDraftInput"
          >
        </div>
      </div>
      <div class="row">
        <div class="col-md-12 d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-outline-secondary" @click="cancelNewProfessionalPosition">Cancel</button>
          <button type="button" class="btn btn-outline-primary" @click="saveNewProfessionalPosition">Save</button>
        </div>
      </div>
    </div>

    <div v-for="(position, index) in professionalPositions" :key="`position-${positionKey(position, index)}`" class="position-item mb-4 p-3 border rounded">
      <div
        class="position-header d-flex justify-content-between align-items-center"
        role="button"
        tabindex="0"
        @click="togglePosition(positionKey(position, index))"
        @keydown.enter.prevent="togglePosition(positionKey(position, index))"
        @keydown.space.prevent="togglePosition(positionKey(position, index))"
      >
        <h6 class="mb-0 border-0 pb-0">{{ positionHeaderTitles[positionKey(position, index)] }}</h6>
        <i class="bi" :class="isPositionExpanded(positionKey(position, index)) ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
      </div>
      <div v-if="isPositionExpanded(positionKey(position, index))" class="row my-3">
        <div class="col-md-12">
          <label class="form-label">Position</label>
          <input
            type="text"
            class="form-control"
            :class="{ 'field-invalid': isPositionFieldInvalid(position, 'position', index) }"
            v-model="position.position"
            @input="markPositionDirty(index)"
          >
        </div>
      </div>
      <div v-if="isPositionExpanded(positionKey(position, index))" class="row mb-3">
        <div class="col-md-6">
          <label class="form-label">Start Date</label>
          <input
            type="date"
            class="form-control"
            :class="{ 'field-invalid': isPositionFieldInvalid(position, 'startDate', index) }"
            v-model="position.startDate"
            @input="markPositionDirty(index)"
          >
        </div>
        <div class="col-md-6">
          <label class="form-label">End Date</label>
          <input
            type="date"
            class="form-control"
            :class="{ 'field-invalid': isPositionFieldInvalid(position, 'endDate', index) }"
            v-model="position.endDate"
            @input="markPositionDirty(index)"
          >
        </div>
      </div>
      <div v-if="isPositionExpanded(positionKey(position, index))" class="row">
        <div class="col-md-12 d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-sm btn-outline-primary" :disabled="!positionDirty[index]" @click="saveProfessionalPosition(index)">
            <i class="bi bi-check me-1"></i>Save
          </button>
          <button type="button" class="btn btn-sm btn-outline-danger" @click="removeProfessionalPosition(index)">
            <i class="bi bi-x me-1"></i>Remove
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { parseDateFields } from '@/utils/dateUtils'
import { countries } from '@/services/utils'
import ProfileApiService from '@/services/profile/ProfileApiService'

export default {
  name: 'EducationForm',

  props: {
    formData: {
      type: Object,
      required: true
    },
    profileData: {
      type: Object,
    }
  },
  emits: ['update', 'pending-state-change'],
  data() {
    return {
      isoToName: {
        DZ: 'Algeria',
        AO: 'Angola',
        BJ: 'Benin',
        BW: 'Botswana',
        BF: 'Burkina Faso',
        BI: 'Burundi',
        CM: 'Cameroon',
        CV: 'Cape Verde',
        CF: 'Central African Republic',
        TD: 'Chad',
        KM: 'Comoros',
        CG: 'Republic of the Congo',
        CD: 'Democratic Republic of the Congo',
        DJ: 'Djibouti',
        EG: 'Egypt',
        GQ: 'Equatorial Guinea',
        ER: 'Eritrea',
        SZ: 'Eswatini',
        ET: 'Ethiopia',
        GA: 'Gabon',
        GM: 'Gambia',
        GH: 'Ghana',
        GN: 'Guinea',
        GW: 'Guinea-Bissau',
        CI: 'Ivory Coast',
        KE: 'Kenya',
        LS: 'Lesotho',
        LR: 'Liberia',
        LY: 'Libya',
        MG: 'Madagascar',
        MW: 'Malawi',
        ML: 'Mali',
        MR: 'Mauritania',
        MU: 'Mauritius',
        MA: 'Morocco',
        MZ: 'Mozambique',
        NA: 'Namibia',
        NE: 'Niger',
        NG: 'Nigeria',
        RW: 'Rwanda',
        ST: 'São Tomé & Príncipe',
        SN: 'Senegal',
        SC: 'Seychelles',
        SL: 'Sierra Leone',
        SO: 'Somalia',
        ZA: 'South Africa',
        SS: 'South Sudan',
        SD: 'Sudan',
        TZ: 'Tanzania',
        TG: 'Togo',
        TN: 'Tunisia',
        UG: 'Uganda',
        ZM: 'Zambia',
        ZW: 'Zimbabwe',
      },
      education: [],
      expandedEducationIds: [],
      expandedBoardIds: [],
      expandedPositionIds: [],
      boardMemberships: [],
      professionalPositions: [],
      educationDraft: null,
      educationDraftAttempted: false,
      educationDirty: {},
      educationAttempted: {},
      newEducationInstitutionInput: null,
      boardDraft: null,
      boardDraftAttempted: false,
      boardDirty: {},
      boardAttempted: {},
      newBoardCommitteeInput: null,
      positionDraft: null,
      positionDraftAttempted: false,
      positionDirty: {},
      positionAttempted: {},
      newPositionNameInput: null,
      initialized: false,
    }
  },
  computed: {
    africanCountries() {
      return countries.getCountries()
    },
    hasPendingChanges() {
      return Boolean(this.educationDraft) ||
        Boolean(this.boardDraft) ||
        Boolean(this.positionDraft)
    },
    educationHeaderTitles() {
      return this.education.reduce((titles, edu, index) => {
        const key = this.educationKey(edu, index)
        const degree = this.normalizeHeaderText(edu?.degree)
        const institution = this.normalizeHeaderText(edu?.institution)
        const yearValue = typeof edu?.year === 'string' ? edu.year.trim() : ''
        const yearMatch = yearValue.match(/^(\d{4})/)
        const year = yearMatch ? yearMatch[1] : ''

        if (!degree && !institution && !year) {
          titles[key] = `New Education ${index + 1}`
          return titles
        }

        titles[key] = [degree, institution, year].filter(Boolean).join(' | ')
        return titles
      }, {})
    },
    boardHeaderTitles() {
      return this.boardMemberships.reduce((titles, membership, index) => {
        const key = this.boardKey(membership, index)
        const role = this.normalizeHeaderText(membership?.role)
        const committee = this.normalizeHeaderText(membership?.committee)
        const startDateValue = typeof membership?.startDate === 'string' ? membership.startDate.trim() : ''
        const yearMatch = startDateValue.match(/^(\d{4})/)
        const year = yearMatch ? yearMatch[1] : ''

        if (!role && !committee && !year) {
          titles[key] = `New Board Membership ${index + 1}`
          return titles
        }

        titles[key] = [role, committee, year].filter(Boolean).join(' | ')
        return titles
      }, {})
    },
    positionHeaderTitles() {
      return this.professionalPositions.reduce((titles, position, index) => {
        const key = this.positionKey(position, index)
        const positionName = this.normalizeHeaderText(position?.position)
        const startDateValue = typeof position?.startDate === 'string' ? position.startDate.trim() : ''
        const yearMatch = startDateValue.match(/^(\d{4})/)
        const year = yearMatch ? yearMatch[1] : ''

        if (!positionName && !year) {
          titles[key] = `New Professional Position ${index + 1}`
          return titles
        }

        titles[key] = [positionName, year].filter(Boolean).join(' | ')
        return titles
      }, {})
    }
  },
  watch: {
    // FIXED: Watch for changes in formData and initialize properly
    formData: {
      handler(newData) {
        if (newData && !this.initialized) {
          this.initializeData()
        }
      },
      deep: true,
      immediate: true
    },

    // FIXED: Watch for profileData changes
    profileData: {
      handler(newData) {
        if (newData && !this.initialized) {
          this.initializeData()
        }
      },
      deep: true,
      immediate: true
    },
    hasPendingChanges: {
      handler(isPending) {
        this.$emit('pending-state-change', isPending)
      },
      immediate: true
    }
  },
  methods: {
    normalizeHeaderText(value) {
      if (typeof value !== 'string') return ''

      const cleaned = value.trim().replace(/\s+/g, ' ')
      if (!cleaned) return ''

      return cleaned
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },

    // FIXED: Better initialization logic
    initializeData() {
      this.education = []
      this.boardMemberships = []
      this.professionalPositions = []
      this.educationDirty = {}
      this.educationAttempted = {}
      this.educationDraftAttempted = false
      this.boardDirty = {}
      this.boardAttempted = {}
      this.boardDraftAttempted = false
      this.positionDirty = {}
      this.positionAttempted = {}
      this.positionDraftAttempted = false

      // Initialize from formData first (highest priority)
      if (this.formData?.education && Array.isArray(this.formData.education)) {
        this.education = this.formData.education.map(edu => ({
          id: edu.id || null,
          _localKey: edu.id ? null : `${Date.now()}-${Math.random()}`,
          _isNew: false,
          institution: edu.institution || '',
          degree: edu.degree || '',
          country: this.convertCountry(edu.country),
          year: edu.year || '',
          keywords: edu.keywords || edu.fos || ''
        }))
      }

      if (this.formData?.boardMemberships && Array.isArray(this.formData.boardMemberships)) {
        const memberships = this.formData.boardMemberships.map(membership => ({
          id: membership.id || null,
          _localKey: membership.id ? null : `${Date.now()}-${Math.random()}`,
          _isNew: false,
          committee: membership.committee || '',
          role: membership.role || '',
          startDate: membership.start_date || membership.startDate || '',
          endDate: membership.end_date || membership.endDate || ''
        }))
        this.boardMemberships = parseDateFields(memberships)
      }

      if (this.formData?.professionalPositions && Array.isArray(this.formData.professionalPositions)) {
        const positions = this.formData.professionalPositions.map(position => ({
          id: position.id || null,
          _localKey: position.id ? null : `${Date.now()}-${Math.random()}`,
          _isNew: false,
          position: position.position || '',
          startDate: position.start_date || position.startDate || '',
          endDate: position.end_date || position.endDate || ''
        }))
        this.professionalPositions = parseDateFields(positions)
      }

      // If no data in formData, try to get from profileData
      if (this.profileData?.data) {
        const data = this.profileData.data

        // Load education if not already loaded
        if (this.education.length === 0 && data.education && Array.isArray(data.education)) {
          this.education = data.education.map(edu => ({
            id: edu.id || null,
            _localKey: edu.id ? null : `${Date.now()}-${Math.random()}`,
            _isNew: false,
            institution: edu.institution || '',
            degree: edu.degree || '',
            country: this.convertCountry(edu.country),
            year: edu.year || '',
            keywords: edu.keywords || edu.fos || ''
          }))
        }

        // Load board memberships if not already loaded
        if (this.boardMemberships.length === 0 && data.boardMemberships && Array.isArray(data.boardMemberships)) {
          const memberships = data.boardMemberships.map(membership => ({
            id: membership.id || null,
            _localKey: membership.id ? null : `${Date.now()}-${Math.random()}`,
            _isNew: false,
            committee: membership.committee || '',
            role: membership.role || '',
            startDate: membership.start_date || membership.startDate || '',
            endDate: membership.end_date || membership.endDate || ''
          }))
          this.boardMemberships = parseDateFields(memberships)
        }

        // Load professional positions if not already loaded
        if (this.professionalPositions.length === 0 && data.professionalPositions && Array.isArray(data.professionalPositions)) {
          const positions = data.professionalPositions.map(position => ({
            id: position.id || null,
            _localKey: position.id ? null : `${Date.now()}-${Math.random()}`,
            _isNew: false,
            position: position.position || '',
            startDate: position.start_date || position.startDate || '',
            endDate: position.end_date || position.endDate || ''
          }))
          this.professionalPositions = parseDateFields(positions)
        }
      }

      this.initialized = true
      this.expandedEducationIds = this.education
        .map((edu, index) => (edu._isNew ? this.educationKey(edu, index) : null))
        .filter(Boolean)
      this.expandedBoardIds = this.boardMemberships
        .map((membership, index) => (membership._isNew ? this.boardKey(membership, index) : null))
        .filter(Boolean)
      this.expandedPositionIds = this.professionalPositions
        .map((position, index) => (position._isNew ? this.positionKey(position, index) : null))
        .filter(Boolean)

      // Emit the initial data
      this.emitUpdate()
    },

    // Emit current data to parent
    emitUpdate() {
      const data = {
        education: this.education,
        boardMemberships: this.boardMemberships,
        professionalPositions: this.professionalPositions
      }

      this.$emit('update', data)
    },

    // Education methods
    addEducation() {
      if (this.educationDraft) {
        this.$nextTick(() => {
          this.$refs.newEducationInstitutionInput?.focus()
        })
        return
      }

      this.educationDraft = {
        id: null,
        _localKey: `${Date.now()}-${Math.random()}`,
        _isNew: true,
        institution: '',
        degree: '',
        country: '',
        year: '',
        keywords: ''
      }

      this.$nextTick(() => {
        this.$refs.newEducationInstitutionInput?.focus()
      })
    },

    cancelNewEducation() {
      this.educationDraft = null
    },

    async saveNewEducation() {
      if (!this.isEducationValid(this.educationDraft)) {
        this.educationDraftAttempted = true
        return
      }

      try {
        this.education.unshift({ ...this.educationDraft })
        const educationData = { personal: { education: this.education } }
        console.log('📤 SENDING NEW EDUCATION DATA:', JSON.stringify(educationData, null, 2))
        const response = await ProfileApiService.updateProfile(educationData)
        console.log('📥 RECEIVED RESPONSE:', JSON.stringify(response?.data, null, 2))

        const key = this.educationKey(this.education[0], 0)
        if (key && !this.expandedEducationIds.includes(key)) {
          this.expandedEducationIds.push(key)
        }

        // Refresh form data with latest from API response
        if (response?.data?.personal?.education) {
          this.education = response.data.personal.education.map(edu => ({
            id: edu.id || null,
            _localKey: edu.id ? null : `${Date.now()}-${Math.random()}`,
            _isNew: false,
            institution: edu.institution || '',
            degree: edu.degree || '',
            country: this.convertCountry(edu.country),
            year: edu.year || '',
            keywords: edu.keywords || ''
          }))
        }

        this.educationDraft = null
        this.emitUpdate()
      } catch (error) {
        console.error('Error saving new education:', error)
        this.$emit('error', 'Failed to save education item. Please try again.')
        // Revert the optimistic update
        this.education.shift()
      }
    },

    markEducationDirty(index) {
      this.educationDirty = {
        ...this.educationDirty,
        [index]: true
      }
    },

    async saveEducation(index) {
      if (!this.isEducationValid(this.education[index])) {
        this.educationAttempted = {
          ...this.educationAttempted,
          [index]: true
        }
        return
      }

      try {
        const educationData = { personal: { education: this.education } }
        console.log('📤 SENDING EDUCATION DATA:', JSON.stringify(educationData, null, 2))
        const response = await ProfileApiService.updateProfile(educationData)
        console.log('📥 RECEIVED RESPONSE:', JSON.stringify(response?.data, null, 2))

        this.educationDirty = {
          ...this.educationDirty,
          [index]: false
        }

        // Refresh form data with latest from API response
        if (response?.data?.personal?.education) {
          this.education = response.data.personal.education.map(edu => ({
            id: edu.id || null,
            _localKey: edu.id ? null : `${Date.now()}-${Math.random()}`,
            _isNew: false,
            institution: edu.institution || '',
            degree: edu.degree || '',
            country: this.convertCountry(edu.country),
            year: edu.year || '',
            keywords: edu.keywords || ''
          }))
        }

        this.emitUpdate()
      } catch (error) {
        console.error('Error saving education:', error)
        this.$emit('error', 'Failed to save education item. Please try again.')
      }
    },

    removeEducation(index) {
      const removed = this.education[index]
      this.education.splice(index, 1)
      const removedKey = this.educationKey(removed, index)
      this.expandedEducationIds = this.expandedEducationIds.filter(id => id !== removedKey)
      const nextDirty = {}
      Object.keys(this.educationDirty).forEach((key) => {
        const dirtyIndex = Number(key)
        if (dirtyIndex < index) {
          nextDirty[dirtyIndex] = this.educationDirty[key]
        }
        if (dirtyIndex > index) {
          nextDirty[dirtyIndex - 1] = this.educationDirty[key]
        }
      })
      this.educationDirty = nextDirty
      this.emitUpdate()
    },

    isEducationValid(education) {
      if (!education) return false

      return Boolean(
        String(education.institution || '').trim() &&
        String(education.degree || '').trim() &&
        String(education.country || '').trim() &&
        String(education.year || '').trim()
      )
    },

    isEducationFieldInvalid(education, field, index = null, isDraft = false) {
      const shouldValidate = isDraft ? this.educationDraftAttempted : Boolean(this.educationAttempted[index])
      if (!shouldValidate) return false

      if (field === 'institution') return !String(education?.institution || '').trim()
      if (field === 'degree') return !String(education?.degree || '').trim()
      if (field === 'country') return !String(education?.country || '').trim()
      if (field === 'year') return !String(education?.year || '').trim()
      return false
    },

    handleEducationDraftInput() {
      if (this.educationDraftAttempted && this.isEducationValid(this.educationDraft)) {
        this.educationDraftAttempted = false
      }
    },

    markEducationDirty(index) {
      this.educationDirty = {
        ...this.educationDirty,
        [index]: true
      }

      if (this.educationAttempted[index] && this.isEducationValid(this.education[index])) {
        this.educationAttempted = {
          ...this.educationAttempted,
          [index]: false
        }
      }
    },

    educationKey(education, index) {
      if (!education) return `new-${index}`
      return education.id || education._localKey || `new-${index}`
    },

    toggleEducation(educationKey) {
      if (!educationKey) return

      if (this.expandedEducationIds.includes(educationKey)) {
        this.expandedEducationIds = this.expandedEducationIds.filter(id => id !== educationKey)
      } else {
        this.expandedEducationIds.push(educationKey)
      }
    },

    isEducationExpanded(educationKey) {
      return this.expandedEducationIds.includes(educationKey)
    },

    // Board membership methods
    addBoardMembership() {
      if (this.boardDraft) {
        this.$nextTick(() => {
          this.$refs.newBoardCommitteeInput?.focus()
        })
        return
      }

      this.boardDraft = {
        id: null,
        _localKey: `${Date.now()}-${Math.random()}`,
        _isNew: true,
        committee: '',
        role: '',
        startDate: '',
        endDate: ''
      }

      this.$nextTick(() => {
        this.$refs.newBoardCommitteeInput?.focus()
      })
    },

    cancelNewBoardMembership() {
      this.boardDraft = null
    },

    saveNewBoardMembership() {
      if (!this.isBoardValid(this.boardDraft)) {
        this.boardDraftAttempted = true
        return
      }

      this.boardMemberships.unshift({ ...this.boardDraft })
      const key = this.boardKey(this.boardMemberships[0], 0)
      if (key && !this.expandedBoardIds.includes(key)) {
        this.expandedBoardIds.push(key)
      }

      this.boardDraft = null
      this.emitUpdate()
    },

    markBoardDirty(index) {
      this.boardDirty = {
        ...this.boardDirty,
        [index]: true
      }
    },

    saveBoardMembership(index) {
      if (!this.isBoardValid(this.boardMemberships[index])) {
        this.boardAttempted = {
          ...this.boardAttempted,
          [index]: true
        }
        return
      }

      this.boardDirty = {
        ...this.boardDirty,
        [index]: false
      }
      this.emitUpdate()
    },

    removeBoardMembership(index) {
      const removed = this.boardMemberships[index]
      this.boardMemberships.splice(index, 1)
      const removedKey = this.boardKey(removed, index)
      this.expandedBoardIds = this.expandedBoardIds.filter(id => id !== removedKey)
      const nextDirty = {}
      Object.keys(this.boardDirty).forEach((key) => {
        const dirtyIndex = Number(key)
        if (dirtyIndex < index) {
          nextDirty[dirtyIndex] = this.boardDirty[key]
        }
        if (dirtyIndex > index) {
          nextDirty[dirtyIndex - 1] = this.boardDirty[key]
        }
      })
      this.boardDirty = nextDirty
      this.emitUpdate()
    },

    hasInvalidDateRange(item) {
      if (!item?.startDate || !item?.endDate) return false
      return new Date(item.startDate) > new Date(item.endDate)
    },

    isBoardValid(boardMembership) {
      if (!boardMembership) return false

      return Boolean(
        String(boardMembership.committee || '').trim() &&
        String(boardMembership.role || '').trim() &&
        String(boardMembership.startDate || '').trim() &&
        String(boardMembership.endDate || '').trim() &&
        !this.hasInvalidDateRange(boardMembership)
      )
    },

    isBoardFieldInvalid(membership, field, index = null, isDraft = false) {
      const shouldValidate = isDraft ? this.boardDraftAttempted : Boolean(this.boardAttempted[index])
      if (!shouldValidate) return false

      if (field === 'committee') return !String(membership?.committee || '').trim()
      if (field === 'role') return !String(membership?.role || '').trim()
      if (field === 'startDate') return !String(membership?.startDate || '').trim()
      if (field === 'endDate') return !String(membership?.endDate || '').trim() || this.hasInvalidDateRange(membership)
      return false
    },

    handleBoardDraftInput() {
      if (this.boardDraftAttempted && this.isBoardValid(this.boardDraft)) {
        this.boardDraftAttempted = false
      }
    },

    markBoardDirty(index) {
      this.boardDirty = {
        ...this.boardDirty,
        [index]: true
      }

      if (this.boardAttempted[index] && this.isBoardValid(this.boardMemberships[index])) {
        this.boardAttempted = {
          ...this.boardAttempted,
          [index]: false
        }
      }
    },

    boardKey(membership, index) {
      if (!membership) return `new-${index}`
      return membership.id || membership._localKey || `new-${index}`
    },

    toggleBoard(boardKey) {
      if (!boardKey) return

      if (this.expandedBoardIds.includes(boardKey)) {
        this.expandedBoardIds = this.expandedBoardIds.filter(id => id !== boardKey)
      } else {
        this.expandedBoardIds.push(boardKey)
      }
    },

    isBoardExpanded(boardKey) {
      return this.expandedBoardIds.includes(boardKey)
    },

    // Professional position methods
    addProfessionalPosition() {
      if (this.positionDraft) {
        this.$nextTick(() => {
          this.$refs.newPositionNameInput?.focus()
        })
        return
      }

      this.positionDraft = {
        id: null,
        _localKey: `${Date.now()}-${Math.random()}`,
        _isNew: true,
        position: '',
        startDate: '',
        endDate: ''
      }

      this.$nextTick(() => {
        this.$refs.newPositionNameInput?.focus()
      })
    },

    cancelNewProfessionalPosition() {
      this.positionDraft = null
    },

    saveNewProfessionalPosition() {
      if (!this.isPositionValid(this.positionDraft)) {
        this.positionDraftAttempted = true
        return
      }

      this.professionalPositions.unshift({ ...this.positionDraft })
      const key = this.positionKey(this.professionalPositions[0], 0)
      if (key && !this.expandedPositionIds.includes(key)) {
        this.expandedPositionIds.push(key)
      }

      this.positionDraft = null
      this.emitUpdate()
    },

    markPositionDirty(index) {
      this.positionDirty = {
        ...this.positionDirty,
        [index]: true
      }
    },

    saveProfessionalPosition(index) {
      if (!this.isPositionValid(this.professionalPositions[index])) {
        this.positionAttempted = {
          ...this.positionAttempted,
          [index]: true
        }
        return
      }

      this.positionDirty = {
        ...this.positionDirty,
        [index]: false
      }
      this.emitUpdate()
    },

    removeProfessionalPosition(index) {
      const removed = this.professionalPositions[index]
      this.professionalPositions.splice(index, 1)
      const removedKey = this.positionKey(removed, index)
      this.expandedPositionIds = this.expandedPositionIds.filter(id => id !== removedKey)
      const nextDirty = {}
      Object.keys(this.positionDirty).forEach((key) => {
        const dirtyIndex = Number(key)
        if (dirtyIndex < index) {
          nextDirty[dirtyIndex] = this.positionDirty[key]
        }
        if (dirtyIndex > index) {
          nextDirty[dirtyIndex - 1] = this.positionDirty[key]
        }
      })
      this.positionDirty = nextDirty
      this.emitUpdate()
    },

    isPositionValid(position) {
      if (!position) return false

      return Boolean(
        String(position.position || '').trim() &&
        String(position.startDate || '').trim() &&
        String(position.endDate || '').trim() &&
        !this.hasInvalidDateRange(position)
      )
    },

    isPositionFieldInvalid(position, field, index = null, isDraft = false) {
      const shouldValidate = isDraft ? this.positionDraftAttempted : Boolean(this.positionAttempted[index])
      if (!shouldValidate) return false

      if (field === 'position') return !String(position?.position || '').trim()
      if (field === 'startDate') return !String(position?.startDate || '').trim()
      if (field === 'endDate') return !String(position?.endDate || '').trim() || this.hasInvalidDateRange(position)
      return false
    },

    handlePositionDraftInput() {
      if (this.positionDraftAttempted && this.isPositionValid(this.positionDraft)) {
        this.positionDraftAttempted = false
      }
    },

    markPositionDirty(index) {
      this.positionDirty = {
        ...this.positionDirty,
        [index]: true
      }

      if (this.positionAttempted[index] && this.isPositionValid(this.professionalPositions[index])) {
        this.positionAttempted = {
          ...this.positionAttempted,
          [index]: false
        }
      }
    },

    positionKey(position, index) {
      if (!position) return `new-${index}`
      return position.id || position._localKey || `new-${index}`
    },

    togglePosition(positionKey) {
      if (!positionKey) return

      if (this.expandedPositionIds.includes(positionKey)) {
        this.expandedPositionIds = this.expandedPositionIds.filter(id => id !== positionKey)
      } else {
        this.expandedPositionIds.push(positionKey)
      }
    },

    isPositionExpanded(positionKey) {
      return this.expandedPositionIds.includes(positionKey)
    },

    convertIsoCountry(isoCode) {
      if (!isoCode) return '';

      const name = this.isoToName[isoCode];
      if (!name) return '';

      // Find matching dropdown entry
      const match = this.africanCountries.find(
        c => c.label.toLowerCase() === name.toLowerCase()
      );

      return match ? match.value : '';
    },

    convertCountry(value) {
      if (!value) return '';

      value = String(value).trim();

      // If it's already a 2-letter ISO code, return it as-is
      const upper = value.toUpperCase();
      if (/^[A-Z]{2}$/.test(upper)) {
        // Verify it exists in the countries list
        const exists = this.africanCountries.find(c => c.value === upper);
        if (exists) return upper;
      }

      // Try to find by exact label match in the countries list
      const normalized = value.replace(/-/g, ' ').toLowerCase().trim();
      const exact = this.africanCountries.find(
        c => c.label.toLowerCase() === normalized
      );
      if (exact) return exact.value;

      // Try fuzzy match
      const fuzzy = this.africanCountries.find(
        c => normalized.includes(c.label.toLowerCase())
      );
      if (fuzzy) return fuzzy.value;

      return '';
    }

  },

  // FIXED: Initialize on mount
  mounted() {
    this.$nextTick(() => {
      this.initializeData()
    })
  }
}
</script>

<style scoped>
.form-step {
  min-height: 400px;
}

.form-label {
  font-weight: var(--font-weight-medium);
  margin-bottom: 0.5rem;
}

.form-control,
.form-select {
  border-radius: 6px;
  border: 1px solid #dee2e6;
  padding: 0.75rem;
}

.form-control:focus,
.form-select:focus {
  border-color: #17a2b8;
  box-shadow: 0 0 0 0.2rem rgba(23, 162, 184, 0.25);
}

.education-item,
.board-item,
.position-item {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
}

.education-header {
  cursor: pointer;
  user-select: none;
}

.board-header {
  cursor: pointer;
  user-select: none;
}

.position-header {
  cursor: pointer;
  user-select: none;
}

h6 {
  color: #495057;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 0.5rem;
  font-weight: var(--font-weight-regular);
  font-size: var(--font-size-base);
}

.btn-outline-danger {
  border-color: #dc3545;
  color: #dc3545;
}

.btn-outline-danger:hover {
  background-color: #dc3545;
  color: white;
}

.section-header {
  margin: 2rem 0 1.5rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #17a2b8;
}

.section-title {
  color: #17a2b8;
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-md);
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
</style>
